if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          bullets: this.querySelectorAll('.slider-bullet'), // Added bullets
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        // Event listeners for thumbnails (desktop)
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target));
          });
        }

        // Event listeners for bullets (mobile)
        if (!this.mql.matches) {
          this.elements.bullets.forEach((bullet) => {
            bullet.querySelector('button').addEventListener('click', () => {
              this.setActiveMedia(bullet.dataset.mediaId);
            });
          });
        }

        // Listen for screen size changes to add or remove bullet event listeners
        this.mql.addEventListener('change', this.handleMediaQueryChange.bind(this));
      }

      handleMediaQueryChange(e) {
        if (e.matches) {
          // Screen changed to desktop size
          // Remove bullet event listeners
          this.elements.bullets.forEach((bullet) => {
            bullet.querySelector('button').removeEventListener('click', this.bulletClickHandler);
          });
        } else {
          // Screen changed to mobile size
          // Add bullet event listeners
          this.elements.bullets.forEach((bullet) => {
            bullet.querySelector('button').addEventListener('click', () => {
              this.setActiveMedia(bullet.dataset.mediaId);
            });
          });
        }
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement.dataset.mediaId;

        // Update active thumbnail if available
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        // Update active bullet on mobile
        if (!this.mql.matches) {
          const activeBullet = Array.from(this.elements.bullets).find(
            (bullet) => bullet.dataset.mediaId === mediaId
          );
          this.setActiveBullet(activeBullet);
        }
      }

      setActiveMedia(mediaId) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          const isVertical = this.hasAttribute('data-vertical');
          if (isVertical) {
            activeMedia.parentElement.scrollTo({ top: activeMedia.offsetTop, behavior: 'smooth' });
          } else {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        // Update active thumbnail if available
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          } else {
            // Fallback: Use slide index
            const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
            const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
            const position = index + 1; // Positions are 1-based
            this.announceLiveRegion(activeMedia, position.toString());
          }
        } else {
          // Fallback: Use slide index if thumbnails are not available
          const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
          const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
          const position = index + 1; // Positions are 1-based
          this.announceLiveRegion(activeMedia, position.toString());
        }

        // Update active bullet on mobile
        if (!this.mql.matches) {
          const activeBullet = Array.from(this.elements.bullets).find(
            (bullet) => bullet.dataset.mediaId === mediaId
          );
          this.setActiveBullet(activeBullet);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        const isVertical = this.hasAttribute('data-vertical');
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (isVertical) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      setActiveBullet(activeBullet) {
        if (!this.elements.bullets || !activeBullet) return;

        this.elements.bullets.forEach((bullet) => {
          bullet.classList.remove('is-active');
          bullet.querySelector('button').removeAttribute('aria-current');
        });
        activeBullet.classList.add('is-active');
        activeBullet.querySelector('button').setAttribute('aria-current', 'true');
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}